import { $qs, $qsa } from '@js/utils/querySelector';
import { media } from '@js/utils/mediaQueries';
import { gsap } from 'gsap';



class Menu {
    constructor(trigger) {
        this.trigger = trigger;
        this.isOpen = false;
        this.isAnimating = false;
        this.subMenuAnimating = false;

        this.init();
    }

    init() {
        this.body = $qs('body');
        this.menu = $qs('[data-target="menu"]');
        this.subMenus = $qsa('[data-target="sub-menu"]');
        this.topLevelParents = $qsa('#main-menu > li.menu-item-has-children > a');
        this.menuCover = $qs('[data-target="menu-cover"]');
        
        if (!this.menu) return;

        // get width of widest sub menu
        const widestSubMenu = [...this.subMenus].reduce((a, b) => a.getBoundingClientRect().width > b.getBoundingClientRect().width ? a : b);
        this.widestSubMenuWidth = widestSubMenu.getBoundingClientRect().width;

        gsap.set(this.subMenus, {
            width: this.widestSubMenuWidth,
        });
        
        this.trigger.addEventListener('click', this.toggleMenu.bind(this), false);

        this.topLevelParents.forEach(topLevelParent => topLevelParent.addEventListener('click', (e) => {
            this.openSubMenu(topLevelParent, e);
        }, false));

        this.menuCover.addEventListener('click', (e) => {
            this.trigger.click();
        }, false);
    }

    openMenu() {
        const timeline = gsap.timeline({
            onStart: () => {
                this.isAnimating = true;
                this.isOpen = true;
                this.body.style.overflow = 'hidden';
                this.trigger.dataset.state = 'open';
            },
            onComplete: () => {
                this.isAnimating = false;
            }
        });

        if (this.isAnimating) return;

        timeline
            .addLabel('start')
            .to(this.menu, {
                x: '0%',
                duration: 1,
                ease: 'power4.out',
            }, 'start')
            .to(this.menuCover, {
                autoAlpha: 1,
                pointerEvents: 'auto',
                duration: 0.3,
                ease: 'power3.inOut',
            }, 'start');

        const currentAncestor = [...this.topLevelParents].find(item => item.closest('li').classList.contains('current-menu-ancestor'));

        if (currentAncestor) {
            currentAncestor.click();
        }
    }

    closeMenu() {
        const timeline = gsap.timeline({
            onStart: () => {
                this.isAnimating = true;
                this.isOpen = false;
                this.body.style.overflow = 'auto';
                this.trigger.dataset.state = 'closed';
            },
            onComplete: () => {
                this.isAnimating = false;
                this.menu.dataset.subMenuOpen = 'false';
            }
        });

        timeline
            .addLabel('start')
            .to(this.menu, {
                x: '100%',
                duration: 1,
                ease: 'power4.out',
            }, 'start')
            .to(this.menuCover, {
                autoAlpha: 0,
                pointerEvents: 'none',
                duration: 0.3,
                ease: 'power3.inOut',
            }, 'start');
    }

    toggleMenu() {
        if (this.isAnimating) return;

        if (this.isOpen) {
            this.closeMenu();
        } else {
            this.openMenu();
        }
    }

    openSubMenu(clickedElement, e) {
        e.preventDefault();

        if (this.subMenuAnimating) return;

        const subMenu = $qs('[data-target="sub-menu"]', clickedElement.closest('li'));
        const otherSubMenus = Array.from(this.subMenus).filter(menu => menu !== subMenu);
        const { state } = subMenu.dataset;
        const { subMenuOpen, openSubMenu } = this.menu.dataset;

        const timeline = gsap.timeline({
            onStart: () => {
                this.subMenuAnimating = true;
            },
            onComplete: () => {
                this.subMenuAnimating = false;
                subMenu.dataset.state = state === 'closed' ? 'open' : 'closed';
            }
        });

        gsap.set(this.subMenus, {
            zIndex: 49,
        });

        if (subMenuOpen === 'false') {
            timeline
                .addLabel('start')
                .set(subMenu, {
                    zIndex: 50,
                }, 'start')
                .to(subMenu, {
                    autoAlpha: 1,
                    duration: 0,
                }, 'start')
                .to(this.menu, {
                    x: `-${this.widestSubMenuWidth}px`,
                    duration: 1,
                    ease: 'power4.out',
                }, 'start')
                .add(() => {
                    this.menu.dataset.subMenuOpen = 'true';
                    this.menu.dataset.openSubMenu = clickedElement.dataset.id;
                }, 'start')
        } else if (openSubMenu === clickedElement.dataset.id) {
            timeline
                .addLabel('start')
                .to(this.menu, {
                    x: '0%',
                    duration: 1,
                    ease: 'power4.out',
                }, 'start')
                .add(() => {
                    this.menu.dataset.subMenuOpen = 'false';
                    this.menu.dataset.openSubMenu = '';
                }, 'start')
                .to([subMenu, ...otherSubMenus], {
                    autoAlpha: 0,
                    duration: 0,
                })
        } else {
            timeline
                .addLabel('start')
                .set(subMenu, {
                    zIndex: 50,
                }, 'start')
                .to(subMenu, {
                    autoAlpha: 1,
                    duration: 0,
                }, 'start')
                .from(subMenu, {
                    x: '100%',
                    duration: 1,
                    ease: 'power4.out',
                }, 'start')
                .add(() => {
                    this.menu.dataset.subMenuOpen = 'true';
                    this.menu.dataset.openSubMenu = clickedElement.dataset.id;
                }, 'start')
                .to(otherSubMenus, {
                    autoAlpha: 0,
                    duration: 0
                })
        }
    }

    closeAllSubMenus(e) {
        e.preventDefault();

        const subMenus = $qsa('[data-target="sub-menu"]');
        const timeline = gsap.timeline();

        timeline
            .addLabel('start')
            .to(subMenus, {
                autoAlpha: 0,
                duration: 0.3,
                ease: 'power3.inOut',
            }, 'start')
    }
}

const menuTrigger = $qs('[data-target="menu-trigger"]');

export const menu = new Menu(menuTrigger);